import { useState } from 'react';
import { format } from 'date-fns';
import { Loader2, RefreshCw, CheckCircle2, XCircle, Clock, ChevronDown, ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAdminWebhook, useAdminWebhookLogs } from '@/api/hooks';
import { cn } from '@/lib/utils';
import type { WebhookLogEntry } from '@/api/client';

const PAGE_SIZES = [10, 20, 50] as const;
type PageSize = typeof PAGE_SIZES[number];

export default function AdminWebhookPage() {
  const webhook = useAdminWebhook();
  const logs    = useAdminWebhookLogs();
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [pageSize, setPageSize] = useState<PageSize>(10);
  const [page, setPage] = useState(0);

  const toggleExpand = (id: number) =>
    setExpanded(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const wh = (webhook.data?.webhook as any)?.item ?? webhook.data?.webhook;

  return (
    <div className="space-y-6">
      {/* Config section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold">Webhook Configuration</h1>
          <Button variant="ghost" size="sm" onClick={() => webhook.refetch()} disabled={webhook.isFetching}>
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${webhook.isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {webhook.isLoading && (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {webhook.error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {webhook.error instanceof Error ? webhook.error.message : 'Failed to load webhook config'}
          </div>
        )}

        {wh && (
          <div className="rounded-lg border border-border bg-card shadow-sm">
            <div className="divide-y divide-divider">
              <ConfigRow label="Status">
                {wh.isReady ? (
                  <span className="flex items-center gap-1.5 text-success"><CheckCircle2 className="h-3.5 w-3.5" />Active</span>
                ) : (
                  <span className="flex items-center gap-1.5 text-destructive"><XCircle className="h-3.5 w-3.5" />Not ready — PATCH isReady: true to activate</span>
                )}
              </ConfigRow>
              <ConfigRow label="Webhook ID">{wh.id}</ConfigRow>
              <ConfigRow label="Name">{wh.name ?? '—'}</ConfigRow>
              <ConfigRow label="URL">
                <span className="font-mono text-xs break-all">{wh.webhookUrl ?? wh.webhookURI ?? '—'}</span>
              </ConfigRow>
              <ConfigRow label="Deactivation URL">
                <span className="font-mono text-xs break-all">{wh.deactivationUrl ?? wh.deactivationWebhookUrl ?? '—'}</span>
              </ConfigRow>
              <ConfigRow label="Last modified">
                {wh.lastModifiedDateTime ? format(new Date(wh.lastModifiedDateTime), 'PPp') : '—'}
              </ConfigRow>
              {wh.secretKey != null && (
                <ConfigRow label="Secret key set">
                  <span className={wh.secretKey ? 'text-success' : 'text-destructive'}>
                    {wh.secretKey ? 'Yes' : 'No'}
                  </span>
                </ConfigRow>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Logs section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">Event Log</h2>
            <p className="text-xs text-muted-foreground">
              {logs.data ? `${logs.data.total} total event${logs.data.total !== 1 ? 's' : ''}` : ''}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { logs.refetch(); setPage(0); }} disabled={logs.isFetching}>
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${logs.isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {logs.isLoading && (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading logs…
          </div>
        )}
        {logs.error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {logs.error instanceof Error ? logs.error.message : 'Failed to load logs'}
          </div>
        )}

        {logs.data && logs.data.logs.length === 0 && (
          <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-10 text-center">
            <Clock className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No webhook events received yet.</p>
            <p className="text-xs text-muted-foreground">Events will appear here once the webhook is active and Autotask sends ticket create events.</p>
          </div>
        )}

        {logs.data && logs.data.logs.length > 0 && (() => {
          const allLogs = logs.data.logs;
          const totalPages = Math.ceil(allLogs.length / pageSize);
          const safePage = Math.min(page, totalPages - 1);
          const pageLogs = allLogs.slice(safePage * pageSize, safePage * pageSize + pageSize);
          const from = safePage * pageSize + 1;
          const to = Math.min(safePage * pageSize + pageSize, allLogs.length);

          return (
            <>
              <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr className="text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                      <th className="w-6 px-2 py-2.5" />
                      <th className="px-4 py-2.5">Received</th>
                      <th className="px-4 py-2.5">Event</th>
                      <th className="px-4 py-2.5">Ticket #</th>
                      <th className="px-4 py-2.5">Title</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-divider">
                    {pageLogs.map((log) => (
                      <LogRow
                        key={log.id}
                        log={log}
                        open={expanded.has(log.id)}
                        onToggle={() => toggleExpand(log.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination controls */}
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{from}–{to} of {allLogs.length}</span>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <span>Show</span>
                    {PAGE_SIZES.map((s) => (
                      <button
                        key={s}
                        onClick={() => { setPageSize(s); setPage(0); }}
                        className={cn(
                          'rounded px-2 py-0.5 text-xs',
                          pageSize === s
                            ? 'bg-accent font-medium text-accent-foreground'
                            : 'hover:bg-accent/50',
                        )}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost" size="icon"
                      className="h-7 w-7"
                      disabled={safePage === 0}
                      onClick={() => setPage(safePage - 1)}
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <span className="px-1">{safePage + 1} / {totalPages}</span>
                    <Button
                      variant="ghost" size="icon"
                      className="h-7 w-7"
                      disabled={safePage >= totalPages - 1}
                      onClick={() => setPage(safePage + 1)}
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
}

function ConfigRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-4 px-4 py-3">
      <span className="w-40 shrink-0 text-xs font-medium text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1 text-sm">{children}</span>
    </div>
  );
}

function LogRow({ log, open, onToggle }: { log: WebhookLogEntry; open: boolean; onToggle: () => void }) {
  let prettyPayload: string | null = null;
  if (log.rawPayload) {
    try {
      prettyPayload = JSON.stringify(JSON.parse(log.rawPayload), null, 2);
    } catch {
      prettyPayload = log.rawPayload;
    }
  }

  const isAuthFail = log.eventType?.startsWith('AUTH_FAILED');

  return (
    <>
      <tr
        className={cn('cursor-pointer hover:bg-muted/30', open && 'bg-muted/20')}
        onClick={onToggle}
      >
        <td className="pl-3 py-2.5 text-muted-foreground">
          {open
            ? <ChevronDown className="h-3.5 w-3.5" />
            : <ChevronRight className="h-3.5 w-3.5" />}
        </td>
        <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
          {format(new Date(log.receivedAt), 'PP p')}
        </td>
        <td className="px-4 py-2.5">
          <span className={cn(
            'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
            log.eventType === 'Inserted'
              ? 'bg-success/10 text-success'
              : isAuthFail
              ? 'bg-destructive/10 text-destructive'
              : 'bg-muted text-muted-foreground',
          )}>
            {log.eventType ?? 'Unknown'}
          </span>
        </td>
        <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">
          {log.ticketNumber ?? '—'}
        </td>
        <td className="px-4 py-2.5 max-w-xs truncate text-xs">
          {log.title ?? <span className="italic text-muted-foreground">no title</span>}
        </td>
      </tr>
      {open && (
        <tr className="bg-muted/10">
          <td colSpan={5} className="px-4 pb-3 pt-1">
            {prettyPayload ? (
              <pre className="max-h-80 overflow-auto rounded-md bg-muted px-3 py-2 text-[11px] leading-relaxed text-foreground whitespace-pre-wrap break-all">
                {prettyPayload}
              </pre>
            ) : (
              <p className="py-2 text-xs italic text-muted-foreground">
                No payload captured — this event was logged before payload recording was added.
              </p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
